/**
 * Per-chain stake configuration for the Create Tournament form.
 *
 * Source of truth for:
 * - which chains the form supports
 * - stake presets and custom-amount range per chain
 * - ticker symbol used in UI labels
 *
 * Backend stake_currency mapping (ticker.id) is resolved at runtime by
 * matching the API ticker.chain to a key in this config.
 */

export type ChainKey = 'solana' | 'polygon';

export interface ChainStakeConfig {
  /** Internal key matching ticker.chain from /tickers API */
  chain: ChainKey;
  /** Display name shown on the chain selector tile */
  displayName: string;
  /** Native token short name (POL, SOL); used in stake labels and rate hint */
  ticker: string;
  /** Paid-stake presets shown after the Free option */
  paidPresets: number[];
  /** Inclusive minimum for custom stake input */
  customMin: number;
  /** Inclusive maximum for custom stake input */
  customMax: number;
}

const SOLANA: ChainStakeConfig = {
  chain: 'solana',
  displayName: 'Solana',
  ticker: 'SOL',
  paidPresets: [0.05, 0.1, 0.5],
  customMin: 0.01,
  customMax: 50,
};

const POLYGON: ChainStakeConfig = {
  chain: 'polygon',
  displayName: 'Polygon',
  ticker: 'POL',
  paidPresets: [20, 50, 100],
  customMin: 1,
  customMax: 1000,
};

export const CHAIN_STAKE_CONFIG: Record<ChainKey, ChainStakeConfig> = {
  solana: SOLANA,
  polygon: POLYGON,
};

const VALID_CHAINS: readonly ChainKey[] = ['solana', 'polygon'];
const DEFAULT_ORDER: ChainKey[] = ['solana', 'polygon'];

const parseVisibleChains = (raw?: string): ChainKey[] => {
  if (!raw) return DEFAULT_ORDER;
  const parsed = raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is ChainKey =>
      (VALID_CHAINS as readonly string[]).includes(s),
    );
  return parsed.length > 0 ? parsed : DEFAULT_ORDER;
};

/**
 * Order in which chain tiles render in the selector.
 * Solana first because the launch contest is sponsored by Solana.
 *
 * Optional override via REACT_APP_VISIBLE_CHAINS (CSV of chain keys).
 * Empty/invalid value falls back to all chains.
 */
export const CHAIN_ORDER: ChainKey[] = parseVisibleChains(
  process.env.REACT_APP_VISIBLE_CHAINS,
);

export const DEFAULT_CHAIN: ChainKey = 'solana';

export const getChainConfig = (chain: ChainKey): ChainStakeConfig =>
  CHAIN_STAKE_CONFIG[chain];
